import React from 'react';
import { ViewModel, PubSub } from '@platform/api';
import { isValidType } from '@platform/template';
import SimpleTable from './Table';

const OTHER_COMPLETE = 'OTHER_COMPLETE';
const BEFORE_COMPLETE = 'beforeAreaChangeComplete';

/** 
 * 创建SimpleTable表格
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {object} config simpleTable的配置项
 * @returns {SimpleTableComponent}
 */
export function createSimpleTable(moduleId, config = {}) {
    let store = getStore.call(this, moduleId) || setStore.call(this, moduleId);
    store?.setConfig(config);

    if (!store || !store.getMeta()) {
        return null;
    }

    return <SimpleTable 
        {...config} 
        key={moduleId} 
        id={moduleId} 
        store={store}
        pageScope={this}
    />;
}

/** 
 * 设置表格数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {TableData} data -表格数据
 * @param {boolean} isTop - 2207未实现
 * @param {boolean} shouldForceUpdate - 2207未实现
 * @param {boolean} autoFocus - 是否自动聚焦到第一个元素
 */
export function setAllTableData(moduleId, data, isTop = true, shouldForceUpdate = true, autoFocus = true) {
    let store = getStore.call(this, moduleId),
        pageInfo = store?.getPaginationInfo(),
        { allpks, alltss } = store?.getAllRowsInfo(),
        { crossPageSelect, pkname } = store?.getConfig() || {};

    if(!store || !data) return;

    !this.isUpdatePage && store.getInstance().preventUpdate(true);

    /** @detail 清除排序、筛选、行点击、行选中、行禁用、最后一次check状态 */
    store.setSortInfo();
    store.setFilterInfo();
    store.setClickedRow();
    store.setSelectedCell();
    store.setLastCheckedKey();

    /** 
     * @detail 分页处理
     * 1. TableData携带分页数据，则以数据携带的分页数据为准；
     * 2. 不携带分页信息，rows为空会重置分页信息；
     * 3. 不携带分页数据，rows不为空，在之前数据中没有allpks数据时，将页码重置为首页
     * 4. 其他情况，沿用分页数据
     * */
    if(data.hasOwnProperty('pageInfo')) {
        pageInfo = { ...data.pageInfo, pageIndex: Number(data.pageInfo.pageIndex) };
    } else if(data?.rows?.length === 0) {
        pageInfo = { ...pageInfo, pageIndex: 0, total: 0, totalPage: 1 };
    } else if (!allpks?.length) {
        pageInfo = { ...pageInfo, pageIndex: 0 };
    }
    store.setPaginationInfo(pageInfo);

    // 处理allpks
    if(data.allpks) {
        allpks = data.allpks;
    } else if(data?.rows?.length === 0) {
        allpks = [];
    }
    if(data.alltss) {
        alltss = data.alltss;
    }
    store.setAllRowsInfo({ allpks, alltss });

    /** @detail 设置表格数据 */
    store.setRows(data);

    // 处理checkedKeys
    if (crossPageSelect && pkname) { // 当(crossPageSelect && pkname) === true, useCheckbox会渲染下拉菜单。
        /** @detail 跨页选时，尽可能保留选中状态 */
        let allCheckedKeys = store.getAllCheckedKeys(),
            checkedKeys = new Set(),
            allpksSet = new Set(allpks);

        if (store.isAllChecked()) {
            store.setCheckedKeys(new Set(data.rows.map(row => row.key || row.rowid || row.rowId)), { isCheckedAll: true });
            store.setAllCheckedKeys(new Set(data.allpks || allpks), { isCheckedAll: true });
        } else {
            allCheckedKeys = new Set([...allCheckedKeys].filter(key => allpksSet.has(key)));
            
            data.rows.forEach(row => {
                if (row.values[pkname]) {
                    let pk = row.values[pkname].value;
                    if (allCheckedKeys.has(pk)) {
                        checkedKeys.add(row.key || row.rowid || row.rowId);
                    }
                }
                if (row.selected) {
                    checkedKeys.add(row.rowId);
                }
            });
            
            store.setCheckedKeys(checkedKeys);
            store.setAllCheckedKeys(allCheckedKeys);
        }
    } else {
        /** @detail 非跨页选时清空选中行 */
        let checkedKeys = new Set();
        data.rows.forEach(row => {
            if (row.selected) {
                checkedKeys.add(row.rowId);
            }
        });
        store.setCheckedKeys(checkedKeys);
    }
    
    !this.isUpdatePage && store.getInstance().preventUpdate(false);
    store.forceUpdate();

    // 自动聚焦
    if (autoFocus && data.rows.length) {
        (function autoFocus() {
            requestAnimationFrame(() => {
                // 聚焦第一个元素
                let leafColumnKeys = store.getVisibleLeafColumnKeys().filter(
                    key => isValidType(store.getColumnProps(key, 'itemtype')),
                );

                if (leafColumnKeys.length) {
                    store.focusOnCell(store.getRows()[0]?.key, leafColumnKeys[0]);
                } else {
                    // DOM还没出来（还没有meta的时候），继续轮询
                    autoFocus();
                }
            });
        })();
    }

    // 已知的与其他表格不同：不需要回调selectedChange
}

/** 获取分页信息
 * @memberof SimpleTable
 * @param {ModuleId} moduleId 
 * @returns {{ pageIndex, pageSize }} - 页码：页码从0开始， 每页数量
 */
export function getTablePageInfo(moduleId) {
    let store = getStore.call(this, moduleId),
        info = store.getPaginationInfo();
    return {
        pageIndex: Number(info.pageIndex),
        pageSize: Number(info.pageSize),
    };
}

/** 显示弹窗
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {"add" | "edit"} status TODO:是否有browse状态
 * @param {TableRow} record
 * @param {DataIndex} dataIndex
 */
export function openModel(moduleId, status, record, dataIndex) {
    let store = getStore.call(this, moduleId);

    store?.openSimpleTableModal(status, record, store.getRows()[dataIndex].viewIndex);
}

/** 关闭弹窗
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 */
export function closeModel(moduleId) {
    let store = getStore.call(this, moduleId);
    
    store?.closeSimpleTableModal();
}

/** 设置列的自定义渲染方法
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {function} render
 */
export function setTableRender(moduleId, colKey, render) {
    let store = getStore.call(this, moduleId);
    store.setColumnProps(colKey, { render });
}

/** 设置列精度
 * @memberof SimpleTable
 * @param {{ areacode: ModuleId, fieldcode: ColKey, scale: number }[]} params
 */
export function setColScale(params) {
    params.forEach(param => {
        const { areacode: id, fieldcode: colKey, scale } = param;

        let store = getStore.call(this, id);

        store.setColumnProps(colKey, { scale });
    });
}

/** 获取列的显示属性
 * @memberof SimpleTable
 * @param {ModuleId} moduleId 
 * @param {ColKey} colKey 
 * @returns {boolean} - 显示属性
 */
export function checkVisible(moduleId, colKey) {
    let store = getStore.call(this, moduleId);
    return store.getColumnProps(colKey, 'visible');
}

/** 获取表格数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {boolean} deepClone - 是否进行深拷贝
 * @returns {{ model: boolean, operType: "add" | "edit" | "browse", checkedAll: boolean, allpks: string[], alltss: string[], rows: TableRow[], pageInfo: {pageIndex, pageSize} }} - model:弹窗是否打开，operType:弹窗状态
 */
export function getAllTableData(moduleId, deepClone) {
    let store = getStore.call(this, moduleId);
    let simpleTableModalInfo = store.getSimpleTableModalInfo() || {},
        checkedKeys = store.getCheckedKeys();
    let tableData = {
        rows: store.getRows().map(row => {
            row.selected = checkedKeys.has(row.key || row.rowId);
            return row;
        }),
        pageInfo: store.getPaginationInfo(),
        ...store.getAllRowsInfo(),
        model: !!simpleTableModalInfo.open,
        operType: simpleTableModalInfo.status || 'browse',
        checkedAll: store.isAllChecked(),
    };
    return deepClone ? JSON.parse(JSON.stringify(tableData)) : tableData;
}

/** 设置包含指定数据的行数据
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {{attrcode: CellValue}} record - 指定的列数据
 * @param {TableRow} values - 需要合并的数据
 */
export function setTableValueByKeyAndRecord(moduleId, record, values) {
    let store = getStore.call(this, moduleId);

    let id = record.attrcode.value;
    let rows = store.getRows();

    record = { ...record, ...values };
    rows = rows.map(row => {
        if (row.values.attrcode && id == row.values.attrcode.value) {
            row.values = { ...record };
        }
        return row;
    });

    store.setRows(rows);
}

/** 隐藏列
 * @memberof SimpleTable
 * @param {ModuleId} moduleId 
 * @param {ColKey | ColKey[]} colKeys 
 */
export function hideColByKey(moduleId, colKeys) {
    !Array.isArray(colKeys) && (colKeys = [colKeys]);
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    colKeys.forEach(key => 
        store.setColumnProps(key, { visible: false }, { shouldUpdate: false }),
    );

    rows.forEach(row => 
        colKeys.forEach(key =>
            store.setCellProps(row.key, key, { visible: false }),
        ),
    );

    store.forceUpdate();
}

/** 显示列
 * @memberof SimpleTable
 * @param {ModuleId} moduleId 
 * @param {ColKey | ColKey[]} colKeys 
 */
export function showColByKey(moduleId, colKeys) {
    !Array.isArray(colKeys) && (colKeys = [colKeys]);
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    colKeys.forEach(key => {
        store.setColumnProps(key, { visible: true }, { shouldUpdate: false });
    });

    rows.forEach(row => 
        colKeys.forEach(key =>
            store.setCellProps(row.key, key, { visible: false }),
        ),
    );

    store.forceUpdate();
}

/** 获取选中行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId 
 * @param {boolean} getCrossPageFlag 是否获取跨页选中行
 * @returns {{ data, index, [pageIndex] }[]}
 */
export function getCheckedRows(moduleId, getCrossPageFlag = true) {
    let store = getStore.call(this, moduleId),
        checkedKeys = store.getCheckedKeys(),
        allCheckedKeys = store?.getAllCheckedKeys(),
        allInfo = store.getAllRowsInfo(),
        pageInfo = store.getPaginationInfo(),
        rows = store.getRows(),
        checkedRows = [],
        { crossPageSelect, pkname } = store.getConfig();

    if (getCrossPageFlag && pkname && crossPageSelect) {
        let currentPageRowKeys = new Map(rows.map(row => [row.values[pkname].value, row.key])),
            pkIndexes = {};

        allInfo.allpks.forEach((pk, index) => pkIndexes[pk] = index);

        [...allCheckedKeys].sort((pkA, pkB) => pkIndexes[pkA] - pkIndexes[pkB]).forEach(pk => {
            if(currentPageRowKeys.has(pk)){
                let row = store.getRowData(currentPageRowKeys.get(pk));
                if(!row) return;
                
                checkedRows.push({
                    data: row,
                    index: row.dataIndex,
                    pageIndex: pageInfo?.pageIndex,
                });
            } else {
                let pkIndex = pkIndexes[pk];
                if(pkIndex === -1) return;
                checkedRows.push({
                    data: { pk, ts: allInfo.alltss?.[pkIndex] || '' },
                    index: pkIndex % pageInfo.pageSize,
                    pageIndex: Math.floor(pkIndex / pageInfo.pageSize),
                });
            }
        });
    } else {
        checkedKeys = new Set([...checkedKeys].sort((rowKeyA, rowKeyB) => 
            store.getRowData(rowKeyA) && store.getRowData(rowKeyB) ? store.getRowData(rowKeyA).viewIndex - store.getRowData(rowKeyB).viewIndex : 0,
        ));
        rows.forEach(row => {
            if(checkedKeys.has(row.key)){
                checkedRows.push({ 
                    data: row, 
                    index: row.dataIndex, 
                });
            }
        });
    }
    return checkedRows;
}

/** 选中/取消选中所有行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {boolean} checked
 */
export function selectAllRows(moduleId, checked) {
    let store = getStore.call(this, moduleId);
    if (checked) {
        store.checkAll();
    } else {
        store.setCheckedKeys(new Set());
        store.setAllCheckedKeys(new Set());
    }
    // 已知和其他表格的不同：不需要触发selectedChange
}

/** 反选
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 */
export function reverseSelected(moduleId) {
    let store = getStore.call(this, moduleId);

    store.checkReverse();
    // 已知和其他表格的不同：不需要触发selectedChange
}

/** 通过行index删除行
 * @memberof SimpleTable
 * @param {ModuleId} moduleId
 * @param {DataIndex|DataIndex[]} dataIndexes 
 */
export function deleteTableRowsByIndex(moduleId, dataIndexes) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        checkedKeys = store.getCheckedKeys();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);
    dataIndexes = dataIndexes.map(index => Number(index));

    let deleteRows = rows.filter(row => dataIndexes.includes(row.dataIndex));

    deleteRows.forEach(row => checkedKeys.delete(row.key));

    store.setCheckedKeys(checkedKeys);
    store.deleteRow(deleteRows.map(row => row.key), true);
    // 已知和其他表格的不同：不需要触发selectedChange
}

/** 通过行RowId删除行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {RowKey|RowKey[]} rowKeys 
 */
export function deleteTableRowsByRowId(moduleId, rowKeys) {
    let store = getStore.call(this, moduleId),
        checkedKeys = store.getCheckedKeys();
    
    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);

    rowKeys.forEach(rowKey => checkedKeys.delete(rowKey));
    
    store.setCheckedKeys(checkedKeys);
    store.deleteRow(rowKeys, true);
    // 已知和其他表格的不同：不需要触发selectedChange
}

/** 通过index新增行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {TableRow} data
 * @param {DataIndex} dataIndex 
 */
export function addRow(moduleId, data, dataIndex) {
    let store = getStore.call(this, moduleId);
    
    store.addRow(dataIndex, data.rows, { changeStatus: false });
}

/** 通过弹窗指定项的值
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {string | object} value
 * @param {string} type
 */
export function setTableValueBykey(moduleId, colKey, value, type) {
    let store = getStore.call(this, moduleId);
    
    if (type == 'refer') {
        store.setSimpleTableModalValue(colKey, {
            display: value.refname,
            value: value.refpk,
        });
    } else {
        store.setSimpleTableModalValue(colKey, {
            value: value,
            display: null,
        });
    }
}

/** 设置列的编辑属性
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {boolean} disabled
 */
export function setTableValueDisabled(moduleId, colKey, disabled) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows();
    
    store.setColumnProps(colKey, { disabled });

    rows.forEach(row => store.setCellProps(row.key, colKey, { disabled }));
}

/** 设置列必输属性
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @param {boolean} required
 */
export function setTableValueRequired(moduleId, colKey, required) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows();

    store.setColumnProps(colKey, { required });

    rows.forEach(row => store.setCellProps(row.key, colKey, { required }));
}

/** 通过index聚焦行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {DataIndex} dataIndex
 */
export function focusRowByIndex(moduleId, dataIndex) {
    let store = getStore.call(this, moduleId),
        rowKey = store.getRowKeys()[dataIndex];

    /** @detail 滚动到行 */
    store.scrollIntoView({ rowKey });

    /** @detail 背景色加深 */
    store.setClickedRow(rowKey);
}

/** 通过index更新多行的数据
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {{ data: TableRow, index: dataIndex }[]} rows
 * @param {boolean} isCancelChecked - 是否取消选中状态
 */
export function updateDataByIndexs(moduleId, rows, isCancelChecked = true) {
    let store = getStore.call(this, moduleId);

    let rowKeys = store.getRowKeys(),
        checkedKeys = new Set([...store.getCheckedKeys()]); // 避免直接修改checkedKeys引用
    
    store.setRowData(
        rows.map(({ data: row, index: dataIndex }) => {
            isCancelChecked && checkedKeys.delete(rowKeys[dataIndex]);
            return { ...store.getRowData(rowKeys[dataIndex]), ...row };
        }),
    );

    isCancelChecked && store.setCheckedKeys(checkedKeys);
}

/** 根据rowid设置表格某行某个字段值
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @param {RowKey} rowKey 
 * @param {ColKey} colKey 
 * @param {CellValue} cellValue 
 */
export function setValByKeyAndRowId(moduleId, rowKey, colKey, cellValue) {
    let store = getStore.call(this, moduleId);
    
    store.setCellValue(rowKey, colKey, { value: cellValue });
}

/** 设置某些行的选中状态
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {DataIndex[] | DataIndex} index
 * @param {boolean} checked
 * @param {boolean} isCallBack - 2207未实现
 * @param {{ pattern }} param TODO
 */
export function selectTableRows(moduleId, index, checked, isCallBack, param = {}) {
    let store = getStore.call(this, moduleId);

    let { pattern = 'set' } = param,
        rowKeys = store.getRowKeys(),
        // 根据原方法，应该是dataIndex
        indexes = Array.isArray(index) ? index : [index],
        checkedKeys;
    
    if (checked) {
        switch (pattern) {
            case 'set':
                store.setCheckedKeys(new Set(indexes.map(index => rowKeys[index])));
                break;
            case 'add':
                checkedKeys = store.getCheckedKeys();
                indexes.forEach(index => checkedKeys.add(rowKeys[index]));
                store.setCheckedKeys(checkedKeys);
                break;
            case 'delete':
                checkedKeys = store.getCheckedKeys();
                indexes.forEach(index => checkedKeys.delete(rowKeys[index]));
                store.setCheckedKeys(checkedKeys);
                break;
            case 'clear':
                store.setCheckedKeys(new Set());
                break;
            default:
                break;
        }
    } else {
        checkedKeys = store.getCheckedKeys();
        indexes.forEach(index => checkedKeys.delete(rowKeys[index]));
        store.setCheckedKeys(checkedKeys);
    }
}

/** 获取某页的pks
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {number} pageIndex
 * @param {number} pageSize
 * @returns {Number[]}
 */
export function getPks(moduleId, pageIndex, pageSize) {
    let store = getStore.call(this, moduleId),
        paginationInfo = store.getPaginationInfo();
    
    let { allpks } = store.getAllRowsInfo();

    if(!pageIndex){
        pageIndex = Number(paginationInfo.pageIndex);
    }

    if(!pageSize) {
        pageSize = Number(paginationInfo.pageSize);
    }

    return allpks?.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize) || [];
}

/** 判断表是否有缓存数据
 * @memberof SimpleTable 
 * @param {string} dataSource
 * @returns {object}
 */
export function hasCacheData(dataSource) {
    if (dataSource) {
        let mcData = ViewModel.getData(dataSource);
        if (mcData && mcData.simpleTable && mcData.simpleTable.rows && mcData.simpleTable.rows.length) {
            return true;
        }
    }

    return !!(dataSource && ViewModel.getData(dataSource)?.simpleTable?.rows?.length);
    
}

/** 删除指定的pks
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {number[]} pks
 */
export function deleteCacheId(moduleId, pks) {
    let store = getStore.call(this, moduleId);
    
    Array.isArray(pks) ? (pks = new Set(pks)) : (pks = new Set([pks]));

    let allRowsInfo = store.getAllRowsInfo();
    allRowsInfo.allpks = allRowsInfo.allpks.filter(e => !pks.has(e));

    store.setAllRowsInfo(allRowsInfo);
}

/** 增加pks
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {number[]} pk
 */
export function addCacheId(moduleId, pk) {
    /** @type {THooks} */
    let store = getStore.call(this, moduleId);
    
    let allRowsInfo = store.getAllRowsInfo();
    allRowsInfo.allpks = [...new Set(allRowsInfo.allpks.concat(pk))];

    store.setAllRowsInfo(allRowsInfo);

}

/** 通过rowid更新表格行数据
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {{ rows: TableRows[] }} data 
 */
export function updateTableData(moduleId, data) {
    let store = getStore.call(this, moduleId);
    
    store.setRowData(data.rows);
}

/** 设置当前点击行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {{ index: DataIndex }} data
 */
export function setClickRowIndex(moduleId, data) {
    let store = getStore.call(this, moduleId);
    
    // viewIndex
    store.setClickedRow(store.getVisibleRowKeys()[data.index]);
}

/** 获取当前点击行
 * @memberof SimpleTable 
 * @param {ModuleId} id 
 * @returns {{ record: TableRow, index: dataIndex }}
 */
export function getClickRowIndex(id) {
    let store = getStore.call(this, id);

    let rowKey = store.getClickedRow(),
        row = store.getRowData(rowKey);

    if (!rowKey) {
        return;
    }

    return { record: row, index: row.dataIndex };
}

/** 根据index设置表格某行某个字段值
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @param {DataIndex} dataIndex 
 * @param {ColKey} colKey 
 * @param {CellValue} value 
 */
export function setValByKeyAndIndex(moduleId, dataIndex, colKey, value) {
    let store = getStore.call(this, moduleId);
    
    let rowKeys = store.getRowKeys(),
        rowKey = rowKeys[dataIndex];

    store.setCellValue(rowKey, colKey, { value });
}

/** 差异更新
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @param {TableRow[]} rows 
 */
export function updateDiffDataByIndex(moduleId, rows) {
    let store = getStore.call(this, moduleId);
    
    let rowKeys = store.getRowKeys();
    rows.forEach(({ index, values }) => {
        let rowKey = rowKeys[index];
        let currentRow = store.getRowData(rowKey);
        // 还原以前的写法，不知道直接合并values是否有问题
        Object.keys(values).forEach(key => {
            currentRow.values[key] = { ...currentRow.values[key], ...values[key] };
        });
        store.setRowData(currentRow);
    });
}

/** 
 * 更新表格高度 - 2207已废弃
 * @memberof SimpleTable 
 */
export function updateTableHeight() {
    PubSub.publish(OTHER_COMPLETE, true);
}

/**
 * 更新表格高度 - 2207已废弃
 * @memberof SimpleTable 
 */
export function beforeAreaChangeHeight() {
    PubSub.publishSync(BEFORE_COMPLETE, true);
}

/** 获取当前表格排序信息，包括当前是多列还是单列排序、排序的字段、和排序顺序
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @returns { mode: 'single', sortParam: { field: ColKey, order: 'ascend' | 'default' | 'descend' }[] }
 */
export function getSortParam(moduleId) {
    let store = getStore.call(this, moduleId);

    let sortInfo = store.getSortInfo();
    
    return sortInfo.size ? {
        mode: 'single', // 目前貌似只有single
        sortParam: [...sortInfo].map(([colKey, order]) => {
            if (order === 1) {
                order = 'ascend';
            } else if (order === 0) {
                order = 'default';
            } else {
                order = 'descend';
            }
            return {
                field: colKey,
                order,
            };
        }),
    } : undefined;
}

/** 根据websocket推送值，更新表格数据 head initoutregid (3) [{…}, {…}, {…}]  ORA-20002: wangjfeng trigger error
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @param {ColKey} colKey 
 * @param {TableRow[]} nextRows 
 * @param {string} sagaErrorMessage 
 */
export function updateDataByRefresh(moduleId, colKey, nextRows, sagaErrorMessage) {
    let store = getStore.call(this, moduleId);
    
    if (Array.isArray(nextRows)) {
        let rows = store.getRows();

        let groupedRowsByKey = rows.reduce((group, row) => {
            group[row.values[colKey].value] = row;
            return group;
        }, {});

        nextRows = nextRows.filter(row => row[colKey]).map(row => {
            let rawData = groupedRowsByKey[row[colKey]];
            return {
                ...rawData,
                values: {
                    ...rawData.values,
                    ...Object.entries(row).reduce((values, [key, value]) => {
                        values[key] = { value };
                        return values;
                    }, {}),
                    saga_errormesg: row.saga_status === '1' ? sagaErrorMessage : '',
                },
            };
        });

        store.setRowData(nextRows);
    }
}

/** 
 * 2207未实现
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {*} param1
 */
export function saveFilterCustomColData(moduleId, { attrcode, rowId, value }) {
    
}

/** 记录上一次获取焦点的行，用于恢复焦点 dataIndex
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @param {DataIndex} index 
 */
export function setCacheFocusIndex(moduleId, index) {
    let store = getStore.call(this, moduleId),
        rowKeys = store.getRowKeys();
    
    store.setLastFocusedRow(rowKeys[index]);
}

/**设置列属性
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {Object<ColKey, ColumnProps>} newColumn
 */
export function setColumn(moduleId, newColumn) {
    let store = getStore.call(this, moduleId),
        rows = store.getRows(),
        colKeys = Object.keys(newColumn);

    colKeys.forEach(colKey => store.setColumnProps(colKey, newColumn[colKey]));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, newColumn[colKey])));
}

/** 获取列属性
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {ColKey} colKey
 * @returns {ColumnProps}
 */
export function getColumn(moduleId, colKey) {
    let store = getStore.call(this, moduleId);

    return store.getColumnProps(colKey);
}

/** 在指定位置插入行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {{ index: DataIndex, data: TableRow }[]} record
 * @returns {{ rows: TableRow[] }}
 */
export function insertDataByIndexs(moduleId, record) {
    let store = getStore.call(this, moduleId);
    
    record.forEach(({ index: dataIndex, data: row }) => {
        store.addRow(dataIndex, row, { changeStatus: false });
    });
    // 没找到getData这个方法
    return { rows: store.getData() };
}

/** 在所有行的最后追加数据
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 * @param {string} pkname
 * @param {TableRow[]} nextRows
 */
export function addTableData(moduleId, pkname, nextRows = []) {
    let store = getStore.call(this, moduleId);

    let pageInfo = store.getPaginationInfo(),
        rows = store.getRows(),
        allRowsInfo = store.getAllRowsInfo();

    // 过滤掉已有的行
    nextRows = nextRows.filter(row => {
        let rowKey = row.values[pkname]?.value;
        return !store.getRowData(rowKey);
    });
    
    // 追加allpks
    allRowsInfo.allpks = allRowsInfo.allpks.concat(nextRows.map(row => row.values[pkname]?.value));

    // 追加totalPage
    pageInfo.total += nextRows.length;

    // 追加visibleRows
    if (pageInfo.pageSize > rows.length) {
        // 追加到原始数据后面，不能超过当前pageSize
        nextRows = rows.concat(nextRows.slice(0, pageInfo.pageSize - rows.length));
    }

    setAllTableData.call(this, moduleId, {
        rows,
        pageInfo,
        allpks: allRowsInfo.allpks,
    });
}

/** 设置禁用选中行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @param {{RowKey: boolean}} disabledValues
 */
export function setDisabledRows(moduleId, disabledValues) {
    let store = getStore.call(this, moduleId),
        disabledKeys = store.getDisabledKeys();
    
    Object.entries(disabledValues).forEach(([rowKey, value]) => {
        if (value) {
            disabledKeys.add(rowKey);
        } else {
            disabledKeys.delete(rowKey);
        }
    });

    store.setDisabledKeys(disabledKeys);
}

/** 获取禁用选中行的key值
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId 
 * @returns {string[]}
 */
export function getDisabledRows(moduleId) {
    let store = getStore.call(this, moduleId);

    return [...store.getDisabledKeys()];
}

/** 更新合计行
 * @memberof SimpleTable 
 * @param {ModuleId} moduleId
 */
export function updateTotal(moduleId) {
    let store = getStore.call(this, moduleId);
    store.updateTotal();
}

/**
 * 获取活动单元格（批选）
 * @memberof SimpleTable 
 * @param {*} id 
 * @returns {activeCells:{RowKey: ColKey[]}, activeSpecialCells: {attrcode: string, label: string}}
 */
export function getActiveCells(id) {
    let store = getStore.call(this, id);
    let activeCells = store.getSelectedCell();

    return { 
        activeCells: [...activeCells].reduce((activeCells, [rowKey, colKeys]) => {
            // rowKey为负数是表头的，这里不处理
            if (typeof rowKey === 'string') {
                activeCells[rowKey] = [...colKeys];
            }
            return activeCells;
        }, {}), 
        activeSpecialCells: [...activeCells].reduce((activeCells, [rowKey, colKeys]) => {
            // rowKey为负数是表头的
            if (typeof rowKey === 'number') {
                activeCells = activeCells.concat([...colKeys].map(colKey => {
                    return { attrcode: colKey, label: store.getColumnProps(colKey)?.label };
                }));
            }
            return activeCells;
        }, []),
    };
}

//  ---------------  内部方法调用  ------------------
function getStore(moduleId) {
    return this.tableDataSource[moduleId];
}

function setStore(moduleId) {
    this.tableDataSource[moduleId] = SimpleTable.useSimpleTable();
    return this.tableDataSource[moduleId];
}